import { useMemo } from "react";
import permissionContext from "../../core/context/permissionContext";
import { PermissionLevel } from "../../core/models/PermissionModels";
import accountContext from "../../account/accountContext";

interface PermissionResult {
  /** The raw PermissionLevel for this module (None=0, Read=1, Write=2, Admin=3). */
  level: PermissionLevel;
  /** True if user has at least Read permission (level >= 1). */
  canRead: boolean;
  /** True if user has at least Write permission (level >= 2). */
  canWrite: boolean;
  /** True if user has Admin permission (level === 3). */
  canAdmin: boolean;
  /** True if user has any access at all (level > None). Same as canRead. */
  hasAccess: boolean;
}

/**
 * Hook that returns the current user's permission level for a given module.
 * Reads from the permissions JWT claim via permissionContext.
 *
 * Usage:
 * ```
 * const { canRead, canWrite, canAdmin, hasAccess, level } = usePermission(Modules.Machinery);
 * ```
 */
const usePermission = (module: string): PermissionResult => {
  return useMemo(() => {
    const level = permissionContext.getModulePermission(module);

    return {
      level,
      canRead: level >= PermissionLevel.Read,
      canWrite: level >= PermissionLevel.Write,
      canAdmin: level >= PermissionLevel.Admin,
      hasAccess: level >= PermissionLevel.Read,
    };
  }, [module, accountContext.accessToken]);
};

export default usePermission;
